"use client";

/* eslint-disable @next/next/no-img-element */

import { useState } from "react";

import { useAuthorizedImageUrl } from "@/hooks/useAuthorizedImage";
import { accentVars } from "@/lib/theme/accent-utils";
import { cn } from "@/lib/utils";

export type ChatMessageAvatarVariant = "operator" | "stakeholder" | "external" | "system";
export type ChatMessageAvatarStatus = "online" | "away" | "offline";
export type ChatMessageAvatarSize = "md" | "sm";

export type ChatMessageAvatarProps = {
  initials: string;
  imageUrl?: string;
  alt?: string;
  variant?: ChatMessageAvatarVariant;
  status?: ChatMessageAvatarStatus;
  size?: ChatMessageAvatarSize;
  className?: string;
};

const variantClassMap: Record<ChatMessageAvatarVariant, string> = {
  operator:
    "bg-[var(--token-foreground-default)] text-[var(--token-surface-default)] border-white/30 shadow-[var(--shadow-sm)]",
  stakeholder:
    "bg-[color-mix(in_oklab,var(--token-foreground-default)_80%,transparent)] text-[var(--token-surface-default)] border-white/20 shadow-[var(--shadow-xs)]",
  external:
    "bg-[color-mix(in_oklab,var(--token-surface-default)_15%,transparent)] text-foreground border-border/50",
  system:
    "bg-[color-mix(in_oklab,var(--token-surface-default)_96%,transparent)] text-muted-foreground border-border/60",
};

const sizeClassMap: Record<ChatMessageAvatarSize, string> = {
  md: "h-10 w-10 text-base",
  sm: "h-8 w-8 text-sm",
};

const statusToneStyleMap: Record<ChatMessageAvatarStatus, { backgroundColor: string }> = {
  online: { backgroundColor: accentVars.success.core },
  away: { backgroundColor: accentVars.warning.core },
  offline: {
    backgroundColor: "color-mix(in oklab, var(--token-border-default) 75%, transparent)",
  },
};

export function ChatMessageAvatar({
  initials,
  imageUrl,
  alt,
  variant = "operator",
  status,
  size = "md",
  className,
}: ChatMessageAvatarProps) {
  const [imageFailed, setImageFailed] = useState(false);
  // Namespace-scoped uploads (the system avatar borrows the workspace icon)
  // need a bearer token an <img> can't send; external picture URLs pass through.
  const { url: resolvedImageUrl } = useAuthorizedImageUrl(imageUrl);
  const showImage = Boolean(resolvedImageUrl) && !imageFailed;
  const ariaLabel = alt ?? initials;

  return (
    <span
      className={cn(
        "relative inline-flex items-center justify-center rounded-full border font-semibold uppercase tracking-[0.16em] transition-colors",
        variantClassMap[variant],
        sizeClassMap[size],
        className,
      )}
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      {showImage ? (
        <img
          src={resolvedImageUrl ?? undefined}
          alt={ariaLabel}
          className="h-full w-full rounded-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="pointer-events-none select-none">{initials}</span>
      )}
      {status ? (
        <span
          className={cn(
            "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[color-mix(in_oklab,var(--token-surface-default)_98%,transparent)]",
          )}
          style={statusToneStyleMap[status]}
          aria-hidden
        />
      ) : null}
    </span>
  );
}
